/**
 * Role-based redirect mapping
 * Returns the appropriate redirect path based on user role and location
 */
export const getRedirectPathByRole = (role, location = null) => {
  // Base role mappings
  const roleMap = {
    'admin': '/dashboard',
    'manager': '/ManagerDashboard_A',
    'lounge_managers': '/ManagerDashboard_L_consultz',
    'staff': '/staff-dashboard',
    'cashier': null, // No default - requires location
    'waiter': null, // No default - requires location
    'user': '/WaitersDashboard',
    'super_admin': '/dashboard',
    'staff_admin': '/staff-dashboard',
    'hr': '/hr-dashboard',
    'hr_staff': '/dashboard_staff',
    'store_keeper': '/Dashboard_store_keeper',
    'auditor': '/Dashboard_auditor',
    'requisition_staff': '/Dashboard_HOD_requisition_staff',
    'account': '/Dashboard/Accountant',
    'ict': '/Dashboard/Ict',
    'ceo': '/ceo',
    'kitchen': '/kitchen',
  };
  
  const normalizedRole = role?.toLowerCase?.() || '';
  const normalizedLocation = location?.toLowerCase?.() || '';

  // ============================================================
  // SPECIAL HANDLING FOR CASHIERS - Location is REQUIRED
  // ============================================================
  if (normalizedRole === 'cashier') {
    // Location-based routing for cashiers
    if (normalizedLocation === 'continental') {
      return '/restaurant/ContinentalCashierDashboard';
    } else if (normalizedLocation === 'african') {
      return '/restaurant/AfricanCashierDashboard';
    } else if (normalizedLocation === 'lounge') {
      return '/restaurant/LoungeCashierDashboard';
    } else {
      // Cashier has no valid location - MUST go back to login
      console.warn(`[RoleRedirect] Cashier has no valid location: "${location}", redirecting to login`);
      return '/RestaurantAuth';
    }
  }

  // ============================================================
  // SPECIAL HANDLING FOR WAITERS - Location is REQUIRED
  // ============================================================
  if (normalizedRole === 'waiter') {
    // Location-based routing for waiters
    if (normalizedLocation === 'continental') {
      return '/restaurant/continental';
    } else if (normalizedLocation === 'african') {
      return '/restaurant/african';
    } else if (normalizedLocation === 'lounge') {
      return '/waiter/lounge';
    } else {
      // Waiter has no valid location - MUST go back to login
      console.warn(`[RoleRedirect] Waiter has no valid location: "${location}", redirecting to login`);
      return '/RestaurantAuth';
    }
  }
  
  // ============================================================
  // HANDLE OTHER ROLES
  // ============================================================
  
  // Get path from role map for non-special roles
  const path = roleMap[normalizedRole];
  
  // If path is null or undefined, default to login
  if (!path) {
    console.warn(`[RoleRedirect] Unknown role: "${role}", redirecting to login`);
    return '/login';
  }
  
  return path;
};

/**
 * Gets the redirect path with location parameter
 * Useful for roles that require location context
 */
export const getRedirectPathWithLocation = (role, location, basePath = null) => {
  if (!role) return '/login';
  
  const normalizedRole = role.toLowerCase();
  const normalizedLocation = location?.toLowerCase?.() || '';
  
  // For waiters and cashiers, use the location-based routing
  if (normalizedRole === 'waiter' || normalizedRole === 'cashier') {
    return getRedirectPathByRole(role, location);
  }
  
  // For other roles, use the base path or role map
  if (basePath) return basePath;
  return getRedirectPathByRole(role);
};

/**
 * Validates if a user has a valid location for their role
 * Returns true if the role doesn't require location or has a valid one
 */
export const hasValidLocation = (role, location) => {
  const normalizedRole = role?.toLowerCase?.() || '';
  const normalizedLocation = location?.toLowerCase?.() || '';
  
  // Roles that require location
  const locationRequiredRoles = ['waiter', 'cashier'];
  
  if (locationRequiredRoles.includes(normalizedRole)) {
    // Check if location is valid
    const validLocations = ['african', 'continental', 'lounge'];
    return validLocations.includes(normalizedLocation);
  }
  
  // Roles that don't require location
  return true;
};

/**
 * Gets the display name for a location
 */
export const getLocationDisplayName = (location) => {
  const locationMap = {
    'african': 'African Restaurant',
    'continental': 'Continental Restaurant',
    'lounge': 'Lounge Bar',
  };
  return locationMap[location?.toLowerCase?.()] || location || 'Unknown Location';
};

/**
 * Gets the dashboard title for a user role and location
 */
export const getDashboardTitle = (role, location = null) => {
  const normalizedRole = role?.toLowerCase?.() || '';
  const normalizedLocation = location?.toLowerCase?.() || '';
  
  const titleMap = {
    'admin': 'Administrator Dashboard',
    'manager': 'Manager Dashboard',
   'Lounge_Managers': 'Manager Lounge',
    'staff': 'Staff Dashboard',
    'super_admin': 'Super Admin Dashboard',
    'staff_admin': 'Staff Admin Dashboard',
    'hr': 'HR Dashboard',
    'hr_staff': 'HR Staff Dashboard',
    'store_keeper': 'Store Keeper Dashboard',
    'auditor': 'Auditor Dashboard',
    'requisition_staff': 'Requisition Staff Dashboard',
    'account': 'Accountant Dashboard',
    'ceo': 'CEO Dashboard',
    'kitchen': 'Kitchen Dashboard',
  };
  
  // Special handling for waiter and cashier
  if (normalizedRole === 'waiter' || normalizedRole === 'cashier') {
    const roleDisplay = normalizedRole === 'waiter' ? 'Waiter' : 'Cashier';
    const locationDisplay = getLocationDisplayName(normalizedLocation);
    return `${roleDisplay} Dashboard - ${locationDisplay}`;
  }
  
  return titleMap[normalizedRole] || 'Dashboard';
};

// Default export for convenience
export default getRedirectPathByRole;